import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Select,
  Checkbox,
  CheckboxGroup,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  useToast,
  Box,
  Divider,
  Badge,
  SimpleGrid,
  FormControl,
  FormLabel,
  FormHelperText
} from '@chakra-ui/react';
import { SearchDiscoveryService, type SearchFilters } from '../services/searchDiscoveryService';
import { GENRES } from '../constants/genres';
import { useAuth } from '../contexts/AuthContext';

interface AdvancedSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSearchResults: (results: any[], query: string, filters: SearchFilters) => void;
}

const AdvancedSearchModal: React.FC<AdvancedSearchModalProps> = ({
  isOpen,
  onClose,
  onSearchResults
}) => {
  const { user } = useAuth();
  const toast = useToast();
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({
    contentType: [],
    genres: [],
    yearRange: { min: 1900, max: new Date().getFullYear() },
    durationRange: { min: 0, max: 300 },
    language: [],
    rating: [],
    sortBy: 'relevance'
  });

  const contentTypes = [
    { value: 'movie', label: 'Movies' },
    { value: 'series', label: 'Series' },
    { value: 'short', label: 'Shorts' },
    { value: 'comic', label: 'Comics' },
    { value: 'novel', label: 'Novels' },
    { value: 'book', label: 'Books' }
  ];

  const genres = GENRES as unknown as string[];

  const languages = [
    'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese',
    'Russian', 'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi'
  ];

  const ratings = [
    'G', 'PG', 'PG-13', 'R', 'NC-17', 'TV-Y', 'TV-Y7', 'TV-G',
    'TV-PG', 'TV-14', 'TV-MA'
  ];

  const sortOptions = [
    { value: 'relevance', label: 'Most Relevant' },
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'rating', label: 'Highest Rated' }
  ];

  const handleSearch = async () => {
    if (!searchQuery.trim() && getActiveFiltersCount() === 0) {
      toast({
        title: 'Search query or filters required',
        description: 'Please enter a search term or select at least one filter.',
        status: 'error'
      });
      return;
    }

    try {
      setIsSearching(true);
      const results = await SearchDiscoveryService.searchContent(
        searchQuery.trim() || '', // Allow empty query if filters are present
        filters,
        user?.uid,
        50
      );
      
      onSearchResults(results, searchQuery, filters);
      onClose();
      
      toast({
        title: 'Search completed',
        description: `Found ${results.length} results`,
        status: 'success'
      });
    } catch (error: any) {
      console.error('Search error:', error);
      toast({
        title: 'Search failed',
        description: error.message || 'An error occurred during search.',
        status: 'error'
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleFilterChange = (filterType: keyof SearchFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      contentType: [],
      genres: [],
      yearRange: { min: 1900, max: new Date().getFullYear() },
      durationRange: { min: 0, max: 300 },
      language: [],
      rating: [],
      sortBy: 'relevance'
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.contentType && filters.contentType.length > 0) count++;
    if (filters.genres && filters.genres.length > 0) count++;
    if (filters.language && filters.language.length > 0) count++;
    if (filters.rating && filters.rating.length > 0) count++;
    if (filters.yearRange && (filters.yearRange.min > 1900 || filters.yearRange.max < new Date().getFullYear())) count++;
    if (filters.durationRange && (filters.durationRange.min > 0 || filters.durationRange.max < 300)) count++;
    return count;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white" maxH="90vh" overflowY="auto">
        <ModalHeader>
          <HStack justify="space-between">
            <Text>Advanced Search</Text>
            {getActiveFiltersCount() > 0 && (
              <Badge colorScheme="blue">{getActiveFiltersCount()} filters active</Badge>
            )}
          </HStack>
        </ModalHeader>
        <ModalBody>
          <VStack gap={6} align="stretch">
            {/* Search Query */}
            <FormControl>
              <FormLabel>Search Query</FormLabel>
              <Input
                placeholder="Enter search terms..."
                bg="gray.700"
                border="none"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ bg: 'gray.600' }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </FormControl>

            <Divider />

            {/* Content Type Filter */}
            <FormControl>
              <FormLabel>Content Type</FormLabel>
              <CheckboxGroup
                value={filters.contentType}
                onChange={(value) => handleFilterChange('contentType', value)}
              >
                <SimpleGrid columns={3} gap={2}>
                  {contentTypes.map(type => (
                    <Checkbox key={type.value} value={type.value} colorScheme="purple">
                      {type.label}
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
            </FormControl>

            {/* Genre Filter */}
            <FormControl>
              <FormLabel>Genres</FormLabel>
              <CheckboxGroup
                value={filters.genres}
                onChange={(value) => handleFilterChange('genres', value)}
              >
                <SimpleGrid columns={4} gap={2}>
                  {genres.map(genre => (
                    <Checkbox key={genre} value={genre} colorScheme="purple">
                      {genre}
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
            </FormControl>

            {/* Year Range Filter */}
            <FormControl>
              <FormLabel>Year Range</FormLabel>
              <RangeSlider
                value={[filters.yearRange?.min || 1900, filters.yearRange?.max || new Date().getFullYear()]}
                onChange={(value) => handleFilterChange('yearRange', { min: value[0], max: value[1] })}
                min={1900}
                max={new Date().getFullYear()}
                step={1}
                colorScheme="purple"
              >
                <RangeSliderTrack>
                  <RangeSliderFilledTrack />
                </RangeSliderTrack>
                <RangeSliderThumb index={0} />
                <RangeSliderThumb index={1} />
              </RangeSlider>
              <HStack justify="space-between" mt={2}>
                <Text fontSize="sm" color="gray.400">{filters.yearRange?.min || 1900}</Text>
                <Text fontSize="sm" color="gray.400">{filters.yearRange?.max || new Date().getFullYear()}</Text>
              </HStack>
            </FormControl>

            {/* Duration Range Filter */}
            <FormControl>
              <FormLabel>Duration (minutes)</FormLabel>
              <RangeSlider
                value={[filters.durationRange?.min || 0, filters.durationRange?.max || 300]}
                onChange={(value) => handleFilterChange('durationRange', { min: value[0], max: value[1] })}
                min={0}
                max={300}
                step={5}
                colorScheme="purple"
              >
                <RangeSliderTrack>
                  <RangeSliderFilledTrack />
                </RangeSliderTrack>
                <RangeSliderThumb index={0} />
                <RangeSliderThumb index={1} />
              </RangeSlider>
              <HStack justify="space-between" mt={2}>
                <Text fontSize="sm" color="gray.400">{filters.durationRange?.min || 0} min</Text>
                <Text fontSize="sm" color="gray.400">{filters.durationRange?.max || 300} min</Text>
              </HStack>
            </FormControl>

            {/* Language Filter */}
            <FormControl>
              <FormLabel>Language</FormLabel>
              <CheckboxGroup
                value={filters.language}
                onChange={(value) => handleFilterChange('language', value)}
              >
                <SimpleGrid columns={4} gap={2}>
                  {languages.map(lang => (
                    <Checkbox key={lang} value={lang} colorScheme="purple">
                      {lang}
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
            </FormControl>

            {/* Rating Filter */}
            <FormControl>
              <FormLabel>Content Rating</FormLabel>
              <CheckboxGroup
                value={filters.rating}
                onChange={(value) => handleFilterChange('rating', value)}
              >
                <SimpleGrid columns={4} gap={2}>
                  {ratings.map(rating => (
                    <Checkbox key={rating} value={rating} colorScheme="purple">
                      {rating}
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
            </FormControl>

            {/* Sort By */}
            <FormControl>
              <FormLabel>Sort By</FormLabel>
              <Select
                bg="gray.700"
                border="none"
                color="white"
                _focus={{ bg: 'gray.600' }}
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={clearFilters}>
            Clear Filters
          </Button>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="purple"
            onClick={handleSearch}
            isLoading={isSearching}
            loadingText="Searching..."
            isDisabled={!searchQuery.trim() && getActiveFiltersCount() === 0}
          >
            Search
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdvancedSearchModal;
